import { Component } from '@theme/component';

const SCROLL_THRESHOLD = 300;
const HIDE_TRANSITION_MS = 400;
const FOOTER_SIGNATURE_CLEARANCE = 24;

/**
 * @typedef {Object} BackToTopRefs
 * @property {HTMLButtonElement} button - The back to top control
 */

/**
 * Global back-to-top FAB. Visible after scrolling; smooth scrolls to page top.
 *
 * @extends {Component<BackToTopRefs>}
 */
class BackToTopButton extends Component {
  requiredRefs = ['button'];

  /** @type {number | null} */
  #scrollRafId = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {boolean} */
  #isVisible = false;

  /** @type {number | null} */
  #hideTimeout = null;

  /** @type {(() => void) | null} */
  #onHideTransitionEnd = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    window.addEventListener('scroll', this.#handleWindowScroll, { passive: true, signal });
    window.addEventListener('resize', this.#handleWindowScroll, { passive: true, signal });
    this.#updateVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#clearHideTransition();

    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateVisibility();
    });
  };

  #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #clearHideTransition() {
    if (this.#hideTimeout !== null) {
      clearTimeout(this.#hideTimeout);
      this.#hideTimeout = null;
    }

    if (this.#onHideTransitionEnd) {
      this.removeEventListener('transitionend', this.#onHideTransitionEnd);
      this.#onHideTransitionEnd = null;
    }
  }

  #finishHide() {
    if (this.dataset.visible !== 'false') return;

    this.hidden = true;
    this.inert = true;
  }

  #show() {
    this.#clearHideTransition();
    this.hidden = false;
    this.inert = true;
    this.dataset.visible = 'false';

    if (this.#prefersReducedMotion()) {
      this.dataset.visible = 'true';
      this.inert = false;
      return;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.dataset.visible = 'true';
        this.inert = false;
      });
    });
  }

  #hide() {
    this.#clearHideTransition();
    this.dataset.visible = 'false';
    this.inert = true;

    if (this.#prefersReducedMotion()) {
      this.#finishHide();
      return;
    }

    this.#onHideTransitionEnd = (event) => {
      if (event.target !== this) return;
      if (event.propertyName !== 'transform' && event.propertyName !== 'opacity') return;

      this.#clearHideTransition();
      this.#finishHide();
    };

    this.addEventListener('transitionend', this.#onHideTransitionEnd);

    this.#hideTimeout = window.setTimeout(() => {
      this.#clearHideTransition();
      this.#finishHide();
    }, HIDE_TRANSITION_MS);
  }

  #updateVisibility = () => {
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const shouldShow = scrollTop > SCROLL_THRESHOLD && !this.#isNearFooterSignature();

    if (shouldShow === this.#isVisible) return;

    this.#isVisible = shouldShow;

    if (shouldShow) {
      this.#show();
    } else {
      this.#hide();
    }
  };

  #isNearFooterSignature() {
    const footerSignature = document.querySelector('.page-footer__signature');
    if (!footerSignature) return false;

    const signatureRect = footerSignature.getBoundingClientRect();
    const buttonRect = this.refs.button.getBoundingClientRect();
    const buttonBottom = buttonRect.bottom || window.innerHeight - FOOTER_SIGNATURE_CLEARANCE;

    return signatureRect.top < buttonBottom + FOOTER_SIGNATURE_CLEARANCE;
  }

  scrollToTop() {
    const behavior = this.#prefersReducedMotion() ? 'instant' : 'smooth';

    window.scrollTo({ top: 0, behavior });
  }
}

if (!customElements.get('back-to-top-button')) {
  customElements.define('back-to-top-button', BackToTopButton);
}
